import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { resolveNavigation } from '@platform/entitlements';
import type { PortalModule } from '../../api';
import { ModulePlaceholder } from '../ModulePlaceholder';
import { SectionIndex } from '../SectionIndex';
import { BudgetPlan } from './budget/BudgetPlan';
import { ResourcePlan } from './budget/ResourcePlan';
import { BudgetTracker } from './tracking/BudgetTracker';
import { StreamDefinition } from './workitems/StreamDefinition';
import { AgileWorkspace } from './workitems/agile/AgileWorkspace';
import { ReportTemplates } from './reporting/ReportTemplates';
import { WeeklyStatusMeetings, SteercoDeckCreation } from './reporting/MeetingDecks';
import { AuditTrail } from './audit/AuditTrail';
import { ExecutionDashboard } from './portfolio/ExecutionDashboard';
import { PortfolioExceptions } from './tracking/PortfolioExceptions';
import { ActionNeeded } from './tracking/ActionNeeded';
import { ResourceTracker } from './tracking/ResourceTracker';
import { ExecutionControlCenter } from './tracking/ecc/ExecutionControlCenter';
import { ProjectVisibility } from './settings/ProjectVisibility';
import { RaciDefinition } from './workitems/RaciDefinition';
import { WorkItemAssignment } from './workitems/WorkItemAssignment';
import { WorkItemDefinition } from './workitems/WorkItemDefinition';
import { MeetingIntelligence } from './tracking/MeetingIntelligence';
import { ProjectCompletion } from './tracking/ProjectCompletion';
import { ActivityTimeline } from './tracking/ActivityTimeline';
import { ActionRegister } from './tracking/ActionRegister';
import { UserGuide } from './help/UserGuide';
import { HelpSupport } from './help/HelpSupport';
import { RoadmapDefinition } from './roadmap/RoadmapDefinition';
import { TimelineFinalization } from './roadmap/TimelineFinalization';
import { ExecutiveDashboard } from './executive/ExecutiveDashboard';

/**
 * Project Execution & Project Management — screen resolution, the same shape APR,
 * AI Readiness, SPN and Inventory Registry use.
 *
 * Keyed by the section-key trail, so a route is derived from the catalogue rather than
 * written twice. A key with no entry here falls through to `ModulePlaceholder`, which is
 * how a nav row for a screen still being built behaves — visible, navigable, and honest
 * about being empty, rather than a dead link or a crash.
 */
const SCREENS: Record<string, () => JSX.Element> = {
  /* Portfolio — everything ABOVE a single project. */
  'portfolio/project-setup/project-creation': () => <RoadmapDefinition />,
  'portfolio/project-setup/project-timeline-finalization': () => <TimelineFinalization />,
  'portfolio/portfolio-oversight/execution-dashboard': () => <ExecutionDashboard />,
  'portfolio/portfolio-oversight/portfolio-exceptions': () => <PortfolioExceptions />,
  /* A leaf directly under Program Management — see the catalogue for why it is not a group. */
  'program-management/action-needed': () => <ActionNeeded />,

  /* Program Management — everything WITHIN one, in the source's lifecycle order. */
  'program-management/scope-ownership/stream-definition': () => <StreamDefinition />,
  'program-management/scope-ownership/work-item-definition': () => <WorkItemDefinition />,
  'program-management/scope-ownership/agile-delivery-workspace': () => <AgileWorkspace />,
  'program-management/scope-ownership/work-item-assignment': () => <WorkItemAssignment />,
  'program-management/scope-ownership/raci-definition': () => <RaciDefinition />,
  'program-management/budget-resources/budget-plan': () => <BudgetPlan />,
  'program-management/budget-resources/budget-tracker': () => <BudgetTracker />,
  'program-management/budget-resources/resource-plan': () => <ResourcePlan />,
  'program-management/budget-resources/resource-tracker': () => <ResourceTracker />,
  'program-management/delivery-execution/activity-timeline': () => <ActivityTimeline />,
  'program-management/delivery-execution/execution-control-center': () => <ExecutionControlCenter />,
  'program-management/delivery-execution/action-register': () => <ActionRegister />,
  'program-management/delivery-execution/meeting-intelligence': () => <MeetingIntelligence />,
  'program-management/status-reporting/weekly-status-meetings': () => <WeeklyStatusMeetings />,
  'program-management/status-reporting/steerco-deck-creation': () => <SteercoDeckCreation />,
  'program-management/status-reporting/report-templates': () => <ReportTemplates />,
  /* Between Status Reporting and Closeout & Audit — the owner's placement (2026-09-30). */
  'program-management/executive-dashboard': () => <ExecutiveDashboard />,
  'program-management/closeout-audit/project-completion': () => <ProjectCompletion />,
  'program-management/closeout-audit/audit-trail': () => <AuditTrail />,

  /* The two bottom-group leaves. Keys are the slugs the catalogue derives from their
     labels, the same way every other entry here is keyed. */
  'user-guide': () => <UserGuide />,
  'help-support': () => <HelpSupport />,
  'settings/project-execution/project-visibility': () => <ProjectVisibility />,
};

/**
 * THERE IS NO START MENU FOR THIS MODULE, and that is the source's own decision, not an
 * omission here. `routes/index.js:1108` says it outright:
 *
 *   The Roadmap Execution "Overview" hub (cards page) was removed. Its URL is kept as a
 *   redirect to the Execution Control Center so existing "back" links and bookmarks keep
 *   working.
 *
 * So `/project_management` lands on the Execution Control Center exactly as
 * `/landing/roadmap-execution` does in the source — the nav's own Program Management row
 * points at `/roadmap-execution/tracker` for the same reason (layout-v2.ejs:114).
 */
const START_PATH = '/project_management/program-management/delivery-execution/execution-control-center';

export interface ProjectManagementModuleProps {
  modules: PortalModule[];
}

export function ProjectManagementModule({ modules }: ProjectManagementModuleProps) {
  const module = modules.find((m) => m.id === 'project_management');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;
  return <Content modules={modules} module={module} />;
}

function Content({ modules, module }: { modules: PortalModule[]; module: PortalModule }) {
  const location = useLocation();
  const navigate = useNavigate();

  /*
   * §14.3 — ROUTES ARE THE STABLE IDENTIFIER, so a move leaves a redirect behind.
   *
   * The Portfolio restructure (§14.1.2) moved three screens, and their old paths are in
   * bookmarks, in Portfolio Exceptions' own "Open in Control Center" deep link, and in
   * anything a user pasted into a message. §14.3 is explicit that a route change is only
   * acceptable with "a redirect from the old path"; these are those redirects.
   *
   * They are permanent, not a migration aid: a URL a user has saved should not stop working
   * because the menu was reorganised.
   */
  const MOVED: Record<string, string> = {
    // Portfolio
    '/project_management/roadmap-definition': '/project_management/portfolio/project-setup',
    '/project_management/roadmap-definition/roadmap-definition':
      '/project_management/portfolio/project-setup/project-creation',
    '/project_management/roadmap-definition/timeline-finalization':
      '/project_management/portfolio/project-setup/project-timeline-finalization',
    '/project_management/portfolio/project-setup/timeline-finalization':
      '/project_management/portfolio/project-setup/project-timeline-finalization',
    '/project_management/deployment-tracking/portfolio-exceptions':
      '/project_management/portfolio/portfolio-oversight/portfolio-exceptions',
    // Scope & Ownership
    '/project_management/work-items-ownership': '/project_management/program-management/scope-ownership',
    '/project_management/work-items-ownership/stream-definition':
      '/project_management/program-management/scope-ownership/stream-definition',
    '/project_management/work-items-ownership/work-item-definition':
      '/project_management/program-management/scope-ownership/work-item-definition',
    '/project_management/work-items-ownership/agile-delivery-workspace':
      '/project_management/program-management/scope-ownership/agile-delivery-workspace',
    '/project_management/work-items-ownership/work-item-assignment':
      '/project_management/program-management/scope-ownership/work-item-assignment',
    '/project_management/work-items-ownership/raci-definition':
      '/project_management/program-management/scope-ownership/raci-definition',
    // Budget & Resources — the two trackers move here from Deployment Tracking
    '/project_management/budget-resources': '/project_management/program-management/budget-resources',
    '/project_management/budget-resources/budget-plan':
      '/project_management/program-management/budget-resources/budget-plan',
    '/project_management/budget-resources/resource-plan':
      '/project_management/program-management/budget-resources/resource-plan',
    '/project_management/deployment-tracking/budget-tracker':
      '/project_management/program-management/budget-resources/budget-tracker',
    '/project_management/deployment-tracking/resource-tracker':
      '/project_management/program-management/budget-resources/resource-tracker',
    // Delivery Execution
    '/project_management/deployment-tracking': '/project_management/program-management/delivery-execution',
    '/project_management/deployment-tracking/execution-control-center':
      '/project_management/program-management/delivery-execution/execution-control-center',
    '/project_management/deployment-tracking/action-register':
      '/project_management/program-management/delivery-execution/action-register',
    '/project_management/deployment-tracking/meeting-intelligence':
      '/project_management/program-management/delivery-execution/meeting-intelligence',
    // Status Reporting
    '/project_management/status-reporting': '/project_management/program-management/status-reporting',
    '/project_management/status-reporting/report-templates':
      '/project_management/program-management/status-reporting/report-templates',
    '/project_management/status-reporting/weekly-status-meetings':
      '/project_management/program-management/status-reporting/weekly-status-meetings',
    '/project_management/status-reporting/steerco-deck-creation':
      '/project_management/program-management/status-reporting/steerco-deck-creation',
    // Closeout & Audit
    '/project_management/deployment-tracking/project-completion':
      '/project_management/program-management/closeout-audit/project-completion',
    '/project_management/audit-trail': '/project_management/program-management/closeout-audit',
    '/project_management/audit-trail/audit-trail':
      '/project_management/program-management/closeout-audit/audit-trail',
  };
  const moved = MOVED[location.pathname.replace(/\/+$/, '')];
  if (moved) return <Navigate to={`${moved}${location.search}`} replace />;

  const at = resolveNavigation(location.pathname, [module]);

  if (at.trail.length === 0) return <Navigate to={START_PATH} replace />;

  const deepest = at.trail[at.trail.length - 1]!;

  if (!at.section) {
    return (
      <SectionIndex
        title={deepest.label}
        sections={deepest.sections ?? []}
        onOpen={(path) => navigate(path)}
      />
    );
  }

  const key = at.trail.map((s) => s.key).join('/');
  const render = SCREENS[key];
  if (!render) {
    return (
      <ModulePlaceholder
        modules={modules}
        module={module}
        section={{ key: at.section.key, label: at.section.label }}
      />
    );
  }
  return render();
}
